import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Target, ExternalLink, Bell, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import type { IFTriagemItem, IFTriagemOneLiner } from '../hooks/useIFTriagem';
import { fmtBRLShort } from './IFFiduciaryCards';

const SEV_STYLES: Record<string, { border: string; bg: string; dot: string; label: string }> = {
  critico: {
    border: 'border-destructive/30',
    bg: 'bg-destructive/[0.04]',
    dot: 'bg-destructive',
    label: 'Ação imediata',
  },
  atencao: {
    border: 'border-[hsl(var(--warning))]/30',
    bg: 'bg-[hsl(var(--warning))]/[0.04]',
    dot: 'bg-[hsl(var(--warning))]',
    label: 'Monitorar de perto',
  },
  monitorar: {
    border: 'border-border/40',
    bg: 'bg-card',
    dot: 'bg-[hsl(var(--success))]',
    label: 'Monitorar',
  },
};

const CONFIANCA_STYLES: Record<string, { text: string; color: string }> = {
  alta: { text: 'Alta', color: 'text-[hsl(var(--success))]' },
  media: { text: 'Média', color: 'text-[hsl(var(--warning))]' },
  baixa: { text: 'Baixa', color: 'text-muted-foreground' },
};

interface Props {
  items: IFTriagemItem[];
  oneLiner: IFTriagemOneLiner | null;
  maxItems?: number;
}

export const IFTriagemPanel: React.FC<Props> = ({ items, oneLiner, maxItems = 5 }) => {
  const navigate = useNavigate();
  const top = items.slice(0, maxItems);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (top.length === 0) return null;

  const criticals = top.filter(i => i.severidade === 'critico');
  const others = top.filter(i => i.severidade !== 'critico');

  return (
    <Card className="border-border/40 bg-card">
      <CardContent className="pt-5 pb-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-destructive" />
            <h3 className="text-sm font-semibold tracking-tight">Triagem Fiduciária — Onde Agir Agora</h3>
          </div>
          <div className="flex items-center gap-1.5">
            {criticals.length > 0 && (
              <Badge className="bg-destructive text-destructive-foreground text-[10px] px-1.5">
                {criticals.length} crítico{criticals.length > 1 ? 's' : ''}
              </Badge>
            )}
            {others.filter(i => i.severidade === 'atencao').length > 0 && (
              <Badge className="bg-[hsl(var(--warning))] text-white text-[10px] px-1.5">
                {others.filter(i => i.severidade === 'atencao').length} atenção
              </Badge>
            )}
          </div>
        </div>

        {/* Executive one-liner */}
        {oneLiner && (
          <div className="flex items-start gap-2 rounded-md bg-destructive/[0.06] border border-destructive/20 px-3 py-2 mb-4">
            <ShieldAlert className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-xs font-semibold text-foreground leading-relaxed">{oneLiner.text}</p>
          </div>
        )}

        {/* ZONA DE AÇÃO IMEDIATA */}
        {criticals.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-destructive mb-2">🔴 Ação Imediata</p>
            <div className="space-y-3">
              {criticals.map((item) => (
                <TriagemCard key={item.contratoId} item={item} navigate={navigate} expanded={expandedId === item.contratoId} onToggle={() => setExpandedId(expandedId === item.contratoId ? null : item.contratoId)} />
              ))}
            </div>
          </div>
        )}

        {/* ZONA DE MONITORAMENTO */}
        {others.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--warning))] mb-2">🟡 Monitorar de Perto</p>
            <div className="space-y-3">
              {others.map((item) => (
                <TriagemCard key={item.contratoId} item={item} navigate={navigate} expanded={expandedId === item.contratoId} onToggle={() => setExpandedId(expandedId === item.contratoId ? null : item.contratoId)} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/** Single triage card */
function TriagemCard({ item, navigate, expanded, onToggle }: { item: IFTriagemItem; navigate: ReturnType<typeof useNavigate>; expanded: boolean; onToggle: () => void }) {
  const sev = SEV_STYLES[item.severidade] || SEV_STYLES.monitorar;
  const conf = CONFIANCA_STYLES[item.confianca] || CONFIANCA_STYLES.baixa;

  return (
    <div className={`rounded-lg border p-4 ${sev.border} ${sev.bg} transition-colors`}>
      {/* Row 1: Severity + Contract + Capital */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`shrink-0 w-2.5 h-2.5 rounded-full ${sev.dot}`} />
          <span className="text-sm font-semibold truncate">{item.nome}</span>
          <Badge variant="outline" className="text-[10px] shrink-0">{item.contratada}</Badge>
          {(item.alertasAtivos ?? 0) > 0 && (
            <Badge variant="outline" className="text-[10px] shrink-0 border-destructive/30 text-destructive">
              {item.alertasAtivos} alerta{(item.alertasAtivos ?? 0) > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold tabular-nums">{fmtBRLShort(item.capitalExposto)}</p>
          <p className="text-[10px] text-muted-foreground">exposto</p>
        </div>
      </div>

      {/* Row 2: Recommendation */}
      <div className="mb-2.5 pl-[18px]">
        <p className="text-xs font-medium text-foreground leading-relaxed">"{item.recomendacao}"</p>
      </div>

      {/* Row 3: Impact range + Deadline + Confidence + Drivers */}
      <div className="flex items-end justify-between gap-3 pl-[18px]">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {item.impactoMax > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Impacto</p>
              <p className="text-xs font-bold text-destructive tabular-nums">
                {fmtBRLShort(item.impactoMin)} – {fmtBRLShort(item.impactoMax)}
              </p>
            </div>
          )}
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Prazo</p>
            <p className="text-xs font-semibold">{item.prazoAcao}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Confiança</p>
            <p className={`text-xs font-semibold ${conf.color}`}>{conf.text}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Drivers</p>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {item.drivers.map((d, i) => (
                <span key={i} className="inline-flex px-1.5 py-0 rounded text-[10px] bg-muted text-muted-foreground font-medium">{d}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/financiamento/contrato/${item.contratoId}`)}>
            <ExternalLink className="h-3 w-3" />Abrir
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate(`/financiamento/alertas?contrato_id=${item.contratoId}`)}>
            <Bell className="h-3 w-3" />Alertas
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onToggle}>
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Expanded: Score breakdown + Justificativas + Consequência */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/30 pl-[18px] space-y-3">
          {/* Score breakdown */}
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-1">Composição do Score ({item.urgencyScore.toFixed(0)})</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              <ScoreLine label="Prob. atraso" raw={item.breakdown.probAtrasoRaw} contrib={item.breakdown.probAtrasoContrib} />
              <ScoreLine label="Risco" raw={item.breakdown.scoreRiscoRaw} contrib={item.breakdown.scoreRiscoContrib} />
              <ScoreLine label="Inércia" raw={item.breakdown.inerciaRaw} contrib={item.breakdown.inerciaContrib} />
              <ScoreLine label="Tendência" raw={item.breakdown.tendenciaRaw} contrib={item.breakdown.tendenciaContrib} />
            </div>
          </div>

          {/* Justificativas */}
          {item.justificativas.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-1">Por que essa ação?</p>
              <ul className="space-y-0.5">
                {item.justificativas.map((j, i) => (
                  <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                    <span className="text-muted-foreground">•</span>{j}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Consequência */}
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold mb-1">Se não agir</p>
            <p className="text-xs font-medium text-destructive">{item.consequencia}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreLine({ label, raw, contrib }: { label: string; raw: number; contrib: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-muted-foreground w-20">{label}</span>
      <span className="text-[10px] font-medium tabular-nums w-8 text-right">{raw.toFixed(0)}</span>
      <span className="text-[10px] text-muted-foreground">→</span>
      <span className="text-[10px] font-semibold tabular-nums">+{contrib.toFixed(1)}</span>
    </div>
  );
}
